import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { sentenceCase } from 'sentence-case';
import {
  Badge,
  badgeStyle,
  badgeType,
  btnType,
  Button,
  CheckboxGroup,
  colors,
  fontWeight,
  IconButton,
  Icons,
  Input,
  inputType,
  Text,
} from '../../../../../packages';
import { Drawer, drawerType } from '../../../../../packages/ui/organisms/drawer';
import { AppointmentStatus, AppointmentStatusBagde } from '../../../../../packages/constants/CommonConstants';
import { getTimeZoneText } from '../../../../../constants/CommonConstants';
import { useStyles } from './AppointmentDetail.styles';
import { getEvaluationList } from '../../../../../services/conversation/conversation.service';
import { getPatientModalities } from '../../../../../services/profile/profile.service';
import { formatTheoryName } from '../../../../../utils/CommonUtils';
import { ReportCollapseItems } from '../../../../provider-v2/member-detail/add-new-report/report-items-collapse/ReportItemsCollapse';

const AddEvaluation = ({
  isOpen,
  onClose,
  onAddEvaluation,
  appointment,
  evaluations = [],
  assignedEvaluations,
  onRemoveEvaluation = () => {},
  isExercise = false,
}) => {
  const classes = useStyles({ isExercise });
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState(
    evaluations?.map(ev => ({ label: ev.name, value: ev.evaluationId })),
  );
  const [allData, setAllData] = useState(evaluations || []);
  const [checkedValues, setCheckedValues] = useState(
    evaluations
      ?.filter(ev => assignedEvaluations?.some(ae => ev.evaluationId === ae.id))
      ?.map(ev => ev.evaluationId),
  );
  const [loading, setLoading] = useState(false);
  const [modalities, setModalities] = useState([]);
  const [groupedExercises, setGroupedExercises] = useState({});

  const fetchProviderModalities = async () => {
    try {
      if (appointment?.providerId) {
        const response = await getPatientModalities(appointment.providerId);
        if (response?.data) {
          const fetchedModalities = response.data.modalities || [];
          setModalities(fetchedModalities);
        }
      }
    } catch (error) {
      console.error('Error fetching provider modalities:', error);
      setModalities([]);
    }
  };

  const getAllExercises = async () => {
    try {
      setLoading(true);
      const response = await getEvaluationList({
        pageNumber: 0,
        pageSize: 10000,
        sortBy: 'name',
        status: 'ACTIVE',
        reportingView: true,
        type: 'EXERCISE',
      });
      if (response?.status === 200) {
        const exerciseData = response?.data?.EvaluationSummaryList || [];
        setAllData(exerciseData);
        setOptions(exerciseData?.map(ev => ({ label: ev.name, value: ev.evaluationId })));
        setCheckedValues(
          exerciseData
            ?.filter(ev => assignedEvaluations?.some(ae => ev.evaluationId === ae.id))
            ?.map(ev => ev.evaluationId),
        );
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isExercise) {
      getAllExercises();
      fetchProviderModalities();
    } else {
      setAllData(evaluations || []);
    }
  }, []);

  useEffect(() => {
    if (searchText.trim() === '') {
      setOptions(allData?.map(ev => ({ label: ev.name, value: ev.evaluationId })));
    } else {
      setOptions(
        allData
          ?.filter(ev => ev?.name?.toLowerCase()?.includes(searchText?.toLowerCase()))
          ?.map(ev => ({ label: ev.name, value: ev.evaluationId })),
      );
    }
  }, [searchText]);

  // Group exercises by modality
  useEffect(() => {
    if (isExercise && allData.length > 0) {
      const grouped = {};

      // Filter exercises based on search text
      const filteredExercises =
        searchText.trim() === ''
          ? allData
          : allData.filter(ex => ex?.name?.toLowerCase()?.includes(searchText?.toLowerCase()));

      // If we have modalities, only show exercises that match provider's modalities
      if (modalities.length > 0) {
        // Initialize groups for each provider modality
        modalities.forEach(modality => {
          grouped[modality] = [];
        });

        // Group exercises by matching their theory to provider modalities
        filteredExercises.forEach(exercise => {
          const theory = exercise?.meta?.theory;
          if (theory) {
            const formattedTheory = formatTheoryName(theory);

            // Try to find matching modality
            const matchingModality = modalities.find(mod => {
              const modLower = mod.toLowerCase().trim();
              const theoryLower = formattedTheory.toLowerCase().trim();

              // Try exact match first
              if (modLower === theoryLower) return true;

              // Try if modality contains theory or vice versa
              if (modLower.includes(theoryLower) || theoryLower.includes(modLower)) return true;

              // Try matching without spaces/hyphens
              const modCleaned = modLower.replace(/[\s-]/g, '');
              const theoryCleaned = theoryLower.replace(/[\s-]/g, '');
              if (modCleaned === theoryCleaned) return true;

              return false;
            });

            if (matchingModality) {
              grouped[matchingModality].push(exercise);
            }
          }
        });
      } else {
        // If no modalities, group by exercise theory
        filteredExercises.forEach(exercise => {
          const theory = exercise?.meta?.theory;
          if (theory) {
            const formattedTheory = formatTheoryName(theory);
            if (!grouped[formattedTheory]) {
              grouped[formattedTheory] = [];
            }
            grouped[formattedTheory].push(exercise);
          } else {
            if (!grouped['Other']) {
              grouped['Other'] = [];
            }
            grouped['Other'].push(exercise);
          }
        });
      }

      // Remove empty groups
      Object.keys(grouped).forEach(key => {
        if (grouped[key].length === 0) {
          delete grouped[key];
        }
      });

      setGroupedExercises(grouped);
    }
  }, [allData, modalities, searchText, isExercise]);

  const onChangeCheck = val => {
    const uncheckedEvaluations = checkedValues.filter(id => !val.includes(id));
    uncheckedEvaluations.forEach(evaluationId => {
      onRemoveEvaluation(evaluationId);
    });
    setCheckedValues([...val]);
  };

  const timeZone = getTimeZoneText(appointment.providerTimezone);

  return (
    <Drawer open={isOpen} onClose={onClose} variant={drawerType.NORMAL} className={classes.drawer}>
      <Box className={classes.wrapper}>
        <Box className={classes.header}>
          <IconButton icon="chevron-left" onClick={onClose} className={classes.backBtn} />
          <Text className={classes.headerTitle}>{isExercise ? 'Add exercise' : 'Add evaluation'}</Text>
        </Box>
      </Box>
      {!isExercise && (
        <Box className={classes.evaluation}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Text weight={fontWeight.BOLD} className={classes.title}>
                {appointment.serviceName}
              </Text>
              <Text weight={fontWeight.MEDIUM} className={clsx(classes.text14, classes.duration)}>
                {appointment.serviceDuration} minutes session
              </Text>
            </Box>
            <Badge
              variant={badgeType.FILLED}
              style={AppointmentStatusBagde[appointment.status] || badgeStyle.UNRELATED}
              className={classes.badge}
            >
              {appointment.status === AppointmentStatus.BOOKED
                ? 'Scheduled'
                : sentenceCase(appointment.status || '')}
            </Badge>
          </Box>
          <Box sx={{ marginTop: 2 }}>
            <Text weight={fontWeight.SEMI_BOLD} className={clsx(classes.text14, classes.info)}>
              {dayjs(appointment.startTime).format('MMMM DD, YYYY - hh:mma')}
              &nbsp;-&nbsp;
              {dayjs(appointment.endTime).format('hh:mma')}
              {timeZone ? `- ${timeZone}` : ''}
            </Text>
          </Box>
        </Box>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" paddingY="10px">
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        </Box>
      ) : (
        <Box className={classes.searchWrap}>
          <Input
            variant={inputType.SEARCH}
            placeholder="Search"
            className={classes.searchInput}
            value={searchText}
            onChange={setSearchText}
          />
          <Box mt={3.5}>
            {(() => {
              return isExercise && Object.keys(groupedExercises).length > 0 ? (
                <Box display="flex" flexDirection="column" gap="16px">
                  {Object.entries(groupedExercises)
                    .sort(([modalityA], [modalityB]) => modalityA.localeCompare(modalityB))
                    .map(([modality, exercises]) => (
                      <ReportCollapseItems
                        key={modality}
                        label={modality}
                        open={true}
                        items={exercises.map(ex => ({
                          label: ex.name,
                          value: ex.evaluationId,
                        }))}
                        selectedItems={checkedValues}
                        onChange={onChangeCheck}
                      />
                    ))}
                </Box>
              ) : (
                <CheckboxGroup options={options} onChange={onChangeCheck} value={checkedValues} />
              );
            })()}
          </Box>
        </Box>
      )}
      <Box className={classes.evaluationFooter}>
        <Button variant={btnType.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => onAddEvaluation(checkedValues)} disabled={checkedValues?.length === 0}>
          Add selected
        </Button>
      </Box>
    </Drawer>
  );
};

export default AddEvaluation;
